import { useEffect, useState } from 'react'
import { Form, Grid, Input, InputNumber, Message, Modal, Select, Switch } from '@arco-design/web-react'
import { batchSetChannelBalanceAlertApi } from '@/api/channels'
import { BALANCE_SOURCE_OPTIONS } from '@/utils/channelBalance'

export type ChannelBalanceAlertBatchMode = 'ids' | 'tag'

interface Props {
  visible: boolean
  mode: ChannelBalanceAlertBatchMode
  ids?: number[]
  defaultTag?: string
  onCancel: () => void
  onSuccess?: () => void
}

interface FormValues {
  tag?: string
  balance_source?: string
  balance_warning_threshold_usd?: number
  balance_critical_threshold_usd?: number
  balance_alert_enabled?: boolean
  balance_alert_webhook_url?: string
}

export default function ChannelBalanceAlertBatchModal({
  visible,
  mode,
  ids = [],
  defaultTag = '',
  onCancel,
  onSuccess,
}: Props) {
  const [form] = Form.useForm<FormValues>()
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!visible) return
    form.resetFields()
    form.setFieldsValue({
      tag: defaultTag,
      balance_source: 'estimated',
      balance_alert_enabled: true,
    })
  }, [defaultTag, form, visible])

  const handleOk = async () => {
    try {
      const values = await form.validate()
      if (mode === 'ids' && !ids.length) {
        Message.warning('请先选择渠道')
        return
      }
      if (mode === 'tag' && !values.tag?.trim()) {
        Message.warning('请输入标签名')
        return
      }
      setSubmitting(true)
      await batchSetChannelBalanceAlertApi({
        ids: mode === 'ids' ? ids : undefined,
        tag: mode === 'tag' ? values.tag?.trim() : undefined,
        balance_source: values.balance_source,
        balance_warning_threshold_usd: values.balance_warning_threshold_usd,
        balance_critical_threshold_usd: values.balance_critical_threshold_usd,
        balance_alert_enabled: values.balance_alert_enabled,
        balance_alert_webhook_url: values.balance_alert_webhook_url?.trim() || undefined,
      })
      Message.success(mode === 'tag' ? '已按标签批量更新告警配置' : `已更新 ${ids.length} 个渠道的告警配置`)
      onSuccess?.()
      onCancel()
    } catch (err) {
      if (err instanceof Error && err.message) Message.error(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const title =
    mode === 'tag'
      ? '按标签批量设置余额告警'
      : `批量设置余额告警（已选 ${ids.length} 项）`

  return (
    <Modal
      title={title}
      visible={visible}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={submitting}
      okText="保存"
      style={{ width: 560 }}
      unmountOnExit
    >
      <Form form={form} layout="vertical">
        {mode === 'tag' ? (
          <Form.Item field="tag" label="标签名" rules={[{ required: true, message: '请输入标签名' }]}>
            <Input placeholder="例如：official / backup" />
          </Form.Item>
        ) : null}
        <Grid.Row gutter={12}>
          <Grid.Col span={24}>
            <Form.Item field="balance_source" label="余额管理模式">
              <Select options={BALANCE_SOURCE_OPTIONS} />
            </Form.Item>
          </Grid.Col>
          <Grid.Col span={12}>
            <Form.Item field="balance_warning_threshold_usd" label="低余额提醒阈值 (USD)">
              <InputNumber min={0} precision={6} style={{ width: '100%' }} placeholder="例如 20" />
            </Form.Item>
          </Grid.Col>
          <Grid.Col span={12}>
            <Form.Item field="balance_critical_threshold_usd" label="严重余额阈值 (USD)">
              <InputNumber min={0} precision={6} style={{ width: '100%' }} placeholder="例如 5" />
            </Form.Item>
          </Grid.Col>
        </Grid.Row>
        <Form.Item field="balance_alert_webhook_url" label="Webhook URL">
          <Input.TextArea rows={2} placeholder="可选；留空则使用全局默认 Webhook" />
        </Form.Item>
        <Form.Item field="balance_alert_enabled" label="启用低余额告警" triggerPropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  )
}
